import { useCallback, useEffect, useState } from "react";
import {
  Button,
  ButtonGroup,
  Col,
  Container,
  Form,
  Row,
  Spinner,
} from "react-bootstrap";
import {
  fetchMviewRegions,
  getDisciplines,
  updateCadTemplateFile,
} from "../../helpers/axios/projectsAxios";
import { useDispatch, useSelector } from "react-redux";
import {
  ALL_REGIONS,
  updateGlobalState,
} from "../../helpers/reducers/GlobalReducer";
import Select from "react-select";
import {
  MandatoryExplanation,
  MandatoryFieldIndicator,
} from "../../helpers/constants/Constants";

const CadUpload = () => {
  const { allRegions, superUserDetails } = useSelector(
    (state) => state.globalReducer,
  );
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState(null);
  const [disciplines, setDisciplines] = useState(null);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [selectedDiscipline, setSelectedDiscipline] = useState(null);

  const dispatch = useDispatch();

  const fetchAllRegions = useCallback(async () => {
    const tempRegions = await fetchMviewRegions();
    tempRegions?.data?.sort((a, b) => a.name.localeCompare(b.name));

    dispatch(
      updateGlobalState({
        value: tempRegions.data,
        stateName: ALL_REGIONS,
      }),
    );
  }, [dispatch]);

  useEffect(() => {
    if (allRegions.length === 0) {
      fetchAllRegions();
    }
  }, [allRegions.length, fetchAllRegions]);

  const fileSelectHandler = (event) => {
    let pickedFiles;
    Array.from(event.target.files).forEach((file) => {
      if (!file.name.endsWith(".rvt")) {
        window.alert(`Please select a file with the correct extension: .rvt`);
        pickedFiles = "invalid";
        return;
      }
    });
    if (pickedFiles === "invalid") {
      setFile(null);
      return;
    }
    if (event.target.files) {
      pickedFiles = event.target.files[0];
      setFile(pickedFiles);
    }
  };

  const regionSelectHandler = (e) => {
    setSelectedRegion(e.data);
    fetchDisciplines(e.data.code);
  };

  const fetchDisciplines = async (regionCode) => {
    const disciplines = await getDisciplines(regionCode);
    const sortedData = disciplines.sort((a, b) =>
      a.description.localeCompare(b.description),
    );
    setDisciplines(sortedData);
  };

  const disciplineSelectHandler = (e) => {
    setSelectedDiscipline(e.data);
  };

  const submitTemplate = async () => {
    setUploading(true);
    const formData = new FormData();
    formData.append("selectedRegion", JSON.stringify(selectedRegion));
    formData.append("selectedDiscipline", JSON.stringify(selectedDiscipline));
    formData.append("file", file);
    await updateCadTemplateFile(formData);
    setUploading(false);
    setFile(null);
    setSelectedDiscipline(null);
    setSelectedRegion(null);
  };

  return (
    <>
      <div
        className="project-config-page"
        style={{ paddingLeft: "65px", paddingRight: "0px" }}
      >
        <Container className="project-config-shell cad-upload-shell">
          {superUserDetails?.cadTemplateUpload ? (
            <>
              <div className="cad-upload-header">
                <h4 className="project-config-title">CAD Template Upload</h4>
                <p className="cad-upload-subtitle">
                  Upload and map a regional discipline template for streamlined
                  project setup.
                </p>
              </div>

              <div className="cad-upload-panel">
                <Row className="cad-upload-row">
                  <Col>
                    <Form.Label className="cad-upload-label">
                      Please upload the relevant template
                      <MandatoryFieldIndicator />
                    </Form.Label>
                    <Form.Control
                      className="cad-upload-file-input"
                      type="file"
                      id="templateUpload"
                      accept=".rvt"
                      onChange={(e) => fileSelectHandler(e)}
                    />
                    {file?.name && (
                      <div className="cad-upload-file-chip">{file.name}</div>
                    )}
                  </Col>
                </Row>

                <Row className="cad-upload-row">
                  <Col>
                    <Form.Label className="cad-upload-label">
                      Please select which region the template is for.
                      <MandatoryFieldIndicator />
                    </Form.Label>
                    <Select
                      className="basic-single cad-upload-select"
                      classNamePrefix="acc-project-select"
                      placeholder="Please select a region"
                      options={allRegions
                        // ?.sort((a, b) => a.name.localeCompare(b.name))
                        ?.map((region) => {
                          return {
                            value: region.name,
                            label: region.name,
                            data: region,
                          };
                        })}
                      isSearchable={true}
                      onChange={regionSelectHandler}
                      value={{
                        value: selectedRegion?.name,
                        label: selectedRegion?.name,
                        data: selectedRegion,
                      }}
                    />
                  </Col>
                </Row>

                <Row className="cad-upload-row">
                  <Col>
                    <Form.Label className="cad-upload-label">
                      Please select which discipline the template is for.
                      <MandatoryFieldIndicator />
                    </Form.Label>
                    <Select
                      className="basic-single cad-upload-select"
                      classNamePrefix="acc-project-select"
                      placeholder="Please select a discipline"
                      options={disciplines?.map((o) => {
                        return {
                          value: o.description,
                          label: o.description,
                          data: o,
                        };
                      })}
                      isSearchable={true}
                      onChange={disciplineSelectHandler}
                      value={{
                        value: selectedDiscipline?.description,
                        label: selectedDiscipline?.description,
                        data: selectedDiscipline,
                      }}
                    />
                  </Col>
                </Row>

                <Row className="cad-upload-actions-row">
                  <Col>
                    <ButtonGroup className="cad-upload-actions project-config-actions">
                      <Button
                        className="project-config-action-btn cad-upload-submit-btn"
                        variant="outline-success"
                        size="sm"
                        onClick={(e) => submitTemplate()}
                        disabled={
                          !file || !selectedDiscipline || !selectedRegion
                        }
                      >
                        {uploading ? (
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                          />
                        ) : (
                          <span>Submit Template</span>
                        )}
                      </Button>
                    </ButtonGroup>
                  </Col>
                </Row>
              </div>

              <MandatoryExplanation />
            </>
          ) : (
            <div className="alert alert-danger" role="alert">
              You do not have permission to view this page. Please contact your
              administrator.
            </div>
          )}
        </Container>
      </div>
    </>
  );
};

export default CadUpload;
